import React, { useEffect, useRef, useState, useMemo } from "react";
import css from "./BarGraph.module.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Stack, Typography } from "@mui/material";
import EarningMenu from "../../common/EarningMenu/EarningMenu";
import { useSelector } from "react-redux";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const BarChart = () => {
  const chartRef = useRef(null);
  const { get_job_by_vendor_data, get_vendor_status, get_vendor_data } =
    useSelector((state) => state.serviceManage);

  const [viewMode, setViewMode] = useState("monthly");

  // Destroy the chart instance on unmount to prevent "Canvas already in use" error
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, []);

  const generateGradient = (ctx, chartArea) => {
    const gradient = ctx.createLinearGradient(
      0,
      chartArea.bottom,
      0,
      chartArea.top
    );
    gradient.addColorStop(0, "#F15A24");
    gradient.addColorStop(1, "#982800");
    return gradient;
  };

  // Fixed array for month labels in correct order
  const monthLabelsInOrder = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Default weekly labels
  const defaultWeeklyLabels = ["Week 1", "Week 2", "Week 3", "Week 4"];

  // Memoize the chart data to prevent unnecessary re-renders
  const chartData = useMemo(() => {
    // Get API data with fallbacks
    const apiMonthlyData = get_vendor_data?.monthly_earning || {};
    const apiWeeklyData = get_vendor_data?.weekly || {};

    // Map API data to the fixed month labels array
    const mappedMonthlyData = monthLabelsInOrder.map((month) => {
      return apiMonthlyData[month] !== undefined ? apiMonthlyData[month] : 0;
    });

    // Extract weekly labels and data
    const weeklyLabels = Object.keys(apiWeeklyData).length > 0
      ? Object.keys(apiWeeklyData)
      : defaultWeeklyLabels;
    const weeklyData = Object.keys(apiWeeklyData).length > 0
      ? Object.values(apiWeeklyData)
      : [0, 0, 0, 0];

    return {
      labels: viewMode === "weekly" ? weeklyLabels : monthLabelsInOrder,
      datasets: [
        {
          label: viewMode === "weekly" ? "Weekly Earnings" : "Monthly Earnings",
          data: viewMode === "weekly" ? weeklyData : mappedMonthlyData,
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return "#F15A24"; // Fallback color on first render
            return generateGradient(ctx, chartArea);
          },
          borderColor: "#982800",
          borderWidth: 0,
          borderRadius: 6,
          barPercentage: 0.5,
          categoryPercentage: 0.8,
        },
      ],
    };
  }, [viewMode, get_vendor_data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return `$${value}`; // Format with $ symbol
          },
        },
        suggestedMin: 0,
        suggestedMax: 5000,
      },
    },
    animation: {
      duration: 100, // Disable animations for better performance
    },
    hover: {
      animationDuration: 0, // Disable hover animations
      mode: "nearest", // Only activate nearest item on hover
      intersect: true, // Only trigger hover when directly over the bar
    },
    // interaction: {
    //   mode: "index",
    //   intersect: false, // Improve interaction performance by limiting triggers
    // },
  };

  return (
    <div className={css.total_revenue_wrapper}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography
          className={css.total_rev}
          sx={{
            fontSize: {
              xl: "26px !important",
              lg: "24px !important",
              md: "22px !important",
              sm: "20px !important",
              xs: "18px !important",
            },
          }}
        >
          Amount Earned
        </Typography>

        <EarningMenu
          selectedOption={viewMode}
          setSelectedOption={setViewMode}
        />
      </Stack>
      <Stack alignItems={"center"} sx={{ width: "100%", height: { xs: "220px", sm: "300px", md: "400px" } }}>
        <Bar key={viewMode} ref={chartRef} data={chartData} options={options} />
      </Stack>
    </div>
  );
};

export default BarChart;
